import { useDebouncedCallback } from '@/lib/use-debounced-callback';
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { Dimension, DimensionInput } from '@mss_creator/shared';
import { Button, Icon } from '@pms-digital/design-system';
import { useEffect, useRef, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { DimensionRow } from './dimension-row';

const AUTOSAVE_DEBOUNCE_MS = 500;

interface DimensionFormRow {
  dimensionId?: string;
  title: string;
  description: string;
}

export interface DimensionsFormValues {
  dimensions: DimensionFormRow[];
}

interface DimensionsStepProps {
  initialDimensions: Dimension[];
  /** The company is archived (issue #22): the rows can be opened and read, not changed or saved. */
  readOnly?: boolean;
  onSave: (dimensions: DimensionInput[]) => void;
  onBack: () => void;
  onContinue: () => void;
}

function toDefaultRows(dimensions: Dimension[]): DimensionFormRow[] {
  // No default blank row — the empty state shows only the "Ajouter une
  // dimension" rectangle (design doc decision, 2026-08-18 wizard rework).
  return [...dimensions]
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((dimension) => ({
      dimensionId: dimension.id,
      title: dimension.title,
      description: dimension.description ?? '',
    }));
}

/**
 * Step 3 — dimensions. `useFieldArray` for the dynamic row set, `@dnd-kit`
 * for drag-reorder. No minimum: proceeding with zero dimensions is valid.
 * Blank-title rows are filtered out before every autosave — they never
 * reach the backend. Rows collapse to a single line and expand one at a
 * time (accordion) via `expandedId`; a freshly-added row auto-expands and
 * focuses its title field.
 */
export function DimensionsStep({
  initialDimensions,
  readOnly = false,
  onSave,
  onBack,
  onContinue,
}: DimensionsStepProps) {
  const { control, getValues } = useForm<DimensionsFormValues>({
    defaultValues: { dimensions: toDefaultRows(initialDimensions) },
  });
  const { fields, append, remove, move } = useFieldArray({ control, name: 'dimensions' });

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const justAddedIdRef = useRef<string | null>(null);
  const previousLengthRef = useRef(fields.length);

  useEffect(() => {
    if (fields.length > previousLengthRef.current) {
      const newField = fields.at(-1);
      if (newField) {
        setExpandedId(newField.id);
        justAddedIdRef.current = newField.id;
      }
    }
    previousLengthRef.current = fields.length;
  }, [fields]);

  function buildPayload(): DimensionInput[] {
    const rows = getValues('dimensions');
    return rows
      .filter((row) => row.title.trim().length > 0)
      .map((row, index) => ({
        ...(row.dimensionId ? { id: row.dimensionId } : {}),
        title: row.title.trim(),
        description: row.description.trim().length > 0 ? row.description : null,
        displayOrder: index,
      }));
  }

  const scheduleSave = useDebouncedCallback(() => {
    onSave(buildPayload());
  }, AUTOSAVE_DEBOUNCE_MS);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = fields.findIndex((field) => field.id === active.id);
    const newIndex = fields.findIndex((field) => field.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    move(oldIndex, newIndex);
    scheduleSave();
  }

  function handleRemove(index: number) {
    const removedId = fields[index]?.id;
    if (removedId && expandedId === removedId) setExpandedId(null);
    remove(index);
    scheduleSave();
  }

  function handleAdd() {
    append({ title: '', description: '' });
    scheduleSave();
  }

  function handleToggleExpand(fieldId: string) {
    // A manual toggle (as opposed to the auto-expand `handleAdd` triggers)
    // always clears the "just added" marker — otherwise reopening the same
    // row later would keep stealing focus back to its title field, instead
    // of the auto-focus firing exactly once, right after it was added.
    justAddedIdRef.current = null;
    setExpandedId((previous) => (previous === fieldId ? null : fieldId));
  }

  return (
    <section className="space-y-6">
      <div>
        <h2 className="font-semibold text-h3">Dimensions</h2>
        <p className="text-gray-600 text-small">
          Aucun minimum requis. Cliquez une dimension pour l'éditer, glissez la poignée pour
          réordonner.
        </p>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={fields.map((field) => field.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {fields.map((field, index) => (
              <DimensionRow
                key={field.id}
                sortableId={field.id}
                index={index}
                control={control}
                isExpanded={expandedId === field.id}
                readOnly={readOnly}
                autoFocusTitle={field.id === justAddedIdRef.current && expandedId === field.id}
                onToggleExpand={() => handleToggleExpand(field.id)}
                onRemove={() => handleRemove(index)}
                onFieldChange={scheduleSave}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <button
        type="button"
        onClick={handleAdd}
        disabled={readOnly}
        className="flex items-center justify-center gap-2 rounded-lg border border-gray-200 border-dashed bg-white p-3 font-semibold text-bleu-nuit text-small hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
      >
        <Icon name="add" size={16} />
        Ajouter une dimension
      </button>

      <div className="sticky bottom-0 flex justify-between bg-white pt-4">
        <Button type="button" variant="primary" onClick={onBack}>
          Retour
        </Button>
        <Button
          variant="secondary"
          type="button"
          onClick={() => {
            // Flush on the explicit "leave this step" action — same reason
            // as SujetContexteStep's Suivant: don't leave the parent's
            // lifted state (and the backend save) dependent on the debounce
            // timer having already fired.
            if (!readOnly) onSave(buildPayload());
            onContinue();
          }}
        >
          Suivant
        </Button>
      </div>
    </section>
  );
}
